import React, { useState, useEffect } from "react";
import IconSelect from "./IconSelect";
import ToolsCheckboxes from "./ToolsCheckboxes";
import ImageAttachment from "./ExtensionCreationHeader";
import ExtensionCreationFormInput from "./ExtensionCreationFormInput";
import CustomToolDialog from "./CustomToolDialog";
import CategoryDropdown from "./CategoryDropdown";
import createExtension from "../../../../utils/ExtensionHandlingUtils/createExtension";
import updateExtension from "../../../../utils/ExtensionHandlingUtils/updateExtension";
import useUserDetails from "../../../../utils/auth/UserDetails";
import UserSearchPicker from "../../../common/UserSearchPicker/UserSearchPicker";
import { isCustomToolsFeatureEnabled } from "../../../../utils/general/featureFlagUtils";
import "./ExtensionCreation.css";
import { Box } from "@mui/material";
import ModelDropdown from "./ModelDropdown";
import { toast } from "react-toastify";

const ExtensionCreation = ({
    onExtensionCreated,
    onExtensionUpdated,
    onCancel,
    triggerCreate,
    triggerCancel,
    resetTriggers,
    existingExtension = null,
    isLoading,
    setIsLoading,
}) => {
    const [extensionName, setExtensionName] = useState("");
    const [extensionDescription, setExtensionDescription] = useState("");
    const [extensionInstructions, setExtensionInstructions] = useState("");
    const [extensionIcon, setExtensionIcon] = useState(null);
    const [extensionImage, setExtensionImage] = useState(null);
    const [selectedTools, setSelectedTools] = useState([]);
    const [customTools, setCustomTools] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState("");
    const [isCustomToolDialogVisible, setCustomToolDialogVisible] = useState(false);
    const [isIconDialogVisible, setIconDialogVisible] = useState(false);
    const [customToolName, setCustomToolName] = useState("");
    const [customToolSchema, setCustomToolSchema] = useState("");
    const [editingToolIndex, setEditingToolIndex] = useState(null);
    const [schemaError, setSchemaError] = useState("");
    const [customToolsEnabled, setCustomToolsEnabled] = useState(false);
    const userDetails = useUserDetails();
    const [selectedModel, setSelectedModel] = useState(existingExtension?.models || []);
    const [toolDescription, setToolDescription] = useState("");
    const [toolPolicy, setToolPolicy] = useState("");
    const [authType, setAuthType] = useState("none");
    const [connectorType, setConnectorType] = useState("rest-api");
    const [customApiEndpoint, setCustomApiEndpoint] = useState("");
    const [customApiKey, setCustomApiKey] = useState("");

    // ── Blob Storage extras ──
    const [blobContainerName, setBlobContainerName] = useState("");

    // Access control: collaborators that can see this extension when public.
    // Empty array = visible to everyone (backward compatible with legacy public extensions).
    const [collaborators, setCollaborators] = useState(existingExtension?.collaborators || []);

    useEffect(() => {
        if (triggerCreate) {
            handleSave();
            resetTriggers();
        }
    }, [triggerCreate]);

    useEffect(() => {
        if (triggerCancel) {
            onCancel();
            resetTriggers();
        }
    }, [triggerCancel]);

    const tools = [
        "Search the Web",
        "Search Documents",
        "Generate Picture",
        "Data Analysis",
        "Graph Interpretation",
        "Math Tool",
        "Generate Presentation",
        "Generate Document",
        "Generate Spreadsheet",
    ];
    const disabledTools = ["Data Analysis", "Math Tool"];

    useEffect(() => {
        const fetchFeatureFlag = async () => {
            const isEnabled = await isCustomToolsFeatureEnabled();
            setCustomToolsEnabled(isEnabled);
        };
        fetchFeatureFlag();
    }, []);

    useEffect(() => {
        if (!existingExtension) return;

        setExtensionName(existingExtension.extensionName || "");
        setExtensionDescription(existingExtension.extensionDescription || "");
        setExtensionInstructions(existingExtension.extensionInstructions || "");
        setExtensionIcon(existingExtension.iconClass || null);
        setSelectedCategory(existingExtension.category || "");
        setSelectedTools(existingExtension.tools || []);
        setCustomTools(existingExtension.customTools || []);
        setCollaborators(existingExtension.collaborators || []);

        if (existingExtension.imagePath) {
            setExtensionImage({ file: null, preview: existingExtension.imagePath });
        }
    }, [existingExtension]);

    const handleToolChange = (tool, isChecked) => {
        const updatedSelectedTools = isChecked
            ? [...selectedTools, tool]
            : selectedTools.filter((selectedTool) => selectedTool !== tool);
        setSelectedTools(updatedSelectedTools);
    };

    /** Reset all dialog fields to their defaults. */
    const resetDialogState = () => {
        setCustomToolName("");
        setConnectorType("rest-api");
        setCustomApiEndpoint("");
        setCustomApiKey("");
        setCustomToolSchema("");
        setToolDescription("");
        setToolPolicy("");
        setAuthType("none");
        setBlobContainerName("");
        setEditingToolIndex(null);
        setSchemaError("");
    };

    const handleAddOrEditCustomTool = () => {
        try {
            const parsedSchema = customToolSchema.trim() === "" ? null : JSON.parse(customToolSchema);
            const apiEndpoint = customApiEndpoint.trim() === "" ? null : customApiEndpoint;

            const authentication = { type: authType };
            if (authType === "apiKey") {
                authentication["api-key"] = customApiKey || null;
            } else if (authType === "oauth") {
                authentication["oauthDetails"] = "OAuth configuration options will go here.";
            }

            // Databricks: save access token regardless of authType
            if (connectorType === "databricks" && customApiKey) {
                authentication["api-key"] = customApiKey;
                authentication["type"] = "bearer";
            }

            const toolObject = {
                name: customToolName,
                type: connectorType,
                description: toolDescription,
                policy: toolPolicy,
                "api-endpoint": apiEndpoint || null,
                authentication: authentication,
                "json schema": parsedSchema || null,
                // Blob Storage extras
                ...(connectorType === "blob-storage" && {
                    "blob-container": blobContainerName.trim() || null,
                }),
            };

            const updatedTools = [...customTools];
            if (editingToolIndex !== null) {
                updatedTools[editingToolIndex] = toolObject;
            } else {
                updatedTools.push(toolObject);
            }

            setCustomTools(updatedTools);
            resetDialogState();
            setCustomToolDialogVisible(false);
        } catch (error) {
            setSchemaError("Invalid JSON format. Please correct it.");
        }
    };

    const handleEditCustomTool = (index) => {
        const tool = customTools[index];
        setCustomToolName(tool.name);
        setConnectorType(tool.type || "rest-api");
        setToolDescription(tool.description || "");
        setToolPolicy(tool.policy || "");
        setCustomApiEndpoint(tool["api-endpoint"] || "");
        setCustomToolSchema(tool["json schema"] ? JSON.stringify(tool["json schema"], null, 2) : "");
        setBlobContainerName(tool["blob-container"] || "");
        if (tool.authentication) {
            setAuthType(tool.authentication.type || "none");
            if (tool.authentication.type === "apiKey" || tool.authentication.type === "bearer") {
                setCustomApiKey(tool.authentication["api-key"] || "");
            }
        }
        setEditingToolIndex(index);
        setCustomToolDialogVisible(true);
    };

    const handleDeleteCustomTool = (index) => {
        const updatedTools = customTools.filter((_, toolIndex) => toolIndex !== index);
        setCustomTools(updatedTools);
    };

    const handleImageChange = (imageData) => {
        setExtensionImage(imageData);
    };

    const [errors, setErrors] = useState({
        extensionName: "",
        extensionDescription: "",
        extensionInstructions: "",
        selectedCategory: "",
        extensionImage: "",
        selectedTools: "",
        selectedModel: "",
    });

    const handleSave = async () => {
        const newErrors = {};
        if (!extensionName) newErrors.extensionName = "Name is required.";
        if (!extensionDescription) newErrors.extensionDescription = "Description is required.";
        if (!extensionInstructions) newErrors.extensionInstructions = "Instructions are required.";
        if (!selectedCategory) newErrors.selectedCategory = "Please select a category.";
        if (selectedTools.length === 0 && customTools.length === 0) {
            newErrors.selectedTools = "Please select at least one tool or add a custom tool.";
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        const isNewExtension = !existingExtension?.id;
        const payload = {
            userId: userDetails.userId,
            extensionName,
            extensionDescription,
            extensionInstructions,
            iconClass: extensionIcon || "",
            category: selectedCategory || "",
            tools: selectedTools,
            customTools,
            selectedModel: selectedModel,
            collaborators,
        };

        if (isNewExtension) {
            payload.image = extensionImage?.file || null;
        } else {
            payload.id = existingExtension.id;
            payload.image = extensionImage?.file || existingExtension?.image || null;
        }

        try {
            setIsLoading(true);

            if (isNewExtension) {
                const result = await createExtension(payload);
                onExtensionCreated?.(result);
            } else {
                const updated = await updateExtension(payload);
                if (updated.success) {
                    onExtensionUpdated?.(updated.extension);
                }
            }
        } catch (error) {
            toast.error("An error occurred while creating/updating the extension");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="extension-creation">
            <Box sx={{ display: 'flex', gap: 2, width: 'full' }}>
                <ExtensionCreationFormInput
                    label="Name"
                    value={extensionName}
                    onChange={(e) => {
                        setExtensionName(e.target.value);
                        if (e.target.value.trim()) {
                            setErrors((prev) => ({ ...prev, extensionName: "" }));
                        }
                    }}
                    placeholder="Enter extension name"
                    type="text"
                    error={!!errors.extensionName}
                    helperText={errors.extensionName}
                />

                <CategoryDropdown
                    onCategoryChange={(value) => {
                        setSelectedCategory(value);
                        if (value) {
                            setErrors((prev) => ({ ...prev, selectedCategory: "" }));
                        }
                    }}
                    value={existingExtension?.category}
                    error={!!errors.selectedCategory}
                    helperText={errors.selectedCategory}
                />
            </Box>

            <ExtensionCreationFormInput
                label="Description"
                value={extensionDescription}
                onChange={(e) => {
                    setExtensionDescription(e.target.value);
                    if (e.target.value.trim()) {
                        setErrors((prev) => ({ ...prev, extensionDescription: "" }));
                    }
                }}
                placeholder="Enter extension description"
                type="textarea"
                error={!!errors.extensionDescription}
                helperText={errors.extensionDescription}
            />

            <ExtensionCreationFormInput
                label="Instructions"
                value={extensionInstructions}
                onChange={(e) => {
                    setExtensionInstructions(e.target.value);
                    if (e.target.value.trim()) {
                        setErrors((prev) => ({ ...prev, extensionInstructions: "" }));
                    }
                }}
                placeholder="Enter instructions"
                type="textarea"
                error={!!errors.extensionInstructions}
                helperText={errors.extensionInstructions}
            />

            <div className="form-group" id="image-area">
                <div className="image">
                    <label>Logo <span>(Optional)</span></label>
                    <ImageAttachment imag={extensionImage} onImageChange={handleImageChange} />
                    {errors.extensionImage && <p className="text-red-500 text-xs">{errors.extensionImage}</p>}
                </div>
                <div className="model-dropdown">
                    <ModelDropdown
                        onModelChange={(value) => {
                            setSelectedModel(value);
                            if (value) {
                                setErrors((prev) => ({ ...prev, selectedModel: "" }));
                            }
                        }}
                        value={selectedModel}
                        error={!!errors.selectedModel}
                        helperText={errors.selectedModel}
                    />
                </div>
            </div>

            <div className="form-group">
                <label>Tools Needed <span style={{ color: 'red' }}>*</span></label>
                <ToolsCheckboxes
                    tools={tools}
                    selectedTools={selectedTools}
                    onToolChange={handleToolChange}
                    disabledTools={disabledTools}
                    customTools={customTools}
                    onAddCustomTool={() => setCustomToolDialogVisible(true)}
                    onDeleteCustomTool={handleDeleteCustomTool}
                    onEditCustomTool={handleEditCustomTool}
                />
                {errors.selectedTools && <p style={{ color: 'red' }}>{errors.selectedTools}</p>}
            </div>

            <div className="form-group">
                <UserSearchPicker
                    label="Share with (optional)"
                    placeholder="Search by name or email to add collaborators"
                    helperText="Leave empty to share with everyone when published as public. Add users to restrict visibility."
                    selectedUsers={collaborators}
                    onChange={setCollaborators}
                    excludeUserId={userDetails?.userId || ""}
                />
            </div>

            <CustomToolDialog
                visible={isCustomToolDialogVisible}
                toolName={customToolName}
                apiEndpoint={customApiEndpoint}
                apiKey={customApiKey}
                toolSchema={customToolSchema}
                schemaError={schemaError}
                onHide={() => {
                    setCustomToolDialogVisible(false);
                    resetDialogState();
                }}
                onAddOrEdit={handleAddOrEditCustomTool}
                setToolName={setCustomToolName}
                setApiEndpoint={setCustomApiEndpoint}
                setApiKey={setCustomApiKey}
                setToolSchema={setCustomToolSchema}
                authType={authType}
                setAuthType={setAuthType}
                toolDescription={toolDescription}
                setToolDescription={setToolDescription}
                toolPolicy={toolPolicy}
                setToolPolicy={setToolPolicy}
                connectorType={connectorType}
                setConnectorType={setConnectorType}
                blobContainerName={blobContainerName}
                setBlobContainerName={setBlobContainerName}
            />

            <IconSelect
                visible={isIconDialogVisible}
                onSelect={(icon) => {
                    setExtensionIcon(icon);
                    setIconDialogVisible(false);
                }}
                onClose={() => setIconDialogVisible(false)}
            />
        </div>
    );
};

export default ExtensionCreation;
